import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import api from '../api/axios';
import LayoutWrapper from '../components/Layout/LayoutWrapper';
import { LoadingSpinner, ErrorHandler, EmptyState } from '../components/common/ErrorHandler';
import PermissionGuard, { usePermission } from '../components/Guards/PermissionGuard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Search, UserCircle, Wallet, Plus, TrendingUp, TrendingDown, Eye, Download, FileSpreadsheet, FileText, Printer, Edit, Trash2, X } from 'lucide-react';
import useToast from '../hooks/useToast';
import { generateCashReceipt, generateLedgerReport } from '../components/prints/clubPrintTemplates';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


// Professional Banking Theme
const Theme = {
  primary: '#00478F',
  secondary: '#0066B2',
  accent: '#F68B1F',
  success: '#00A651',
  danger: '#DC3545',
  warning: '#FFC107',
  light: '#F5F7FA',
  white: '#FFFFFF',
  dark: '#1a1a1a',
  border: '#DDE2E8',
};

const resolveProfileImage = (img) => {
  if (!img) return null;
  if (img.startsWith('http')) return img;
  return `${process.env.REACT_APP_BACKEND_URL}${img}`;
};


// Global Styles for responsive and clean scrolling
const GlobalStyles = () => (
  <style>{`
    .hide-scrollbar {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .hide-scrollbar::-webkit-scrollbar {
      display: none;
    }
    
    @media (max-width: 640px) {
      .responsive-text-sm { font-size: 12px; }
      .responsive-padding { padding: 12px; }
    }
    
    @media (min-width: 641px) and (max-width: 1024px) {
      .responsive-text-sm { font-size: 13px; }
      .responsive-padding { padding: 14px; }
    }
    
    * {
      box-sizing: border-box;
    }
  `}</style>
);

// Helper function to check if user is admin
const useIsAdmin = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return user?.role === 'admin' || user?.role === 'Admin' || user?.is_admin === true;
};

// ==================== TRANSACTIONS PAGE ====================
const TransactionsPage = () => {
  const { showSuccess, showError } = useToast();
  const canCreate = usePermission('cashCounter', 'create');
  const isAdmin = useIsAdmin();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    transaction_type: 'Credit',
    payment_mode: 'Cash',
    amount: '',
    note: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/cash-counter/transactions');
      setTransactions(response.data);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await api.get('/api/members/search/autocomplete', { params: { q: query } });
      const eligibleMembers = response.data.filter(m => m.membership_type !== 'Guest');

      const membersWithBalance = await Promise.all(
        eligibleMembers.map(async (member) => {
          try {
            const ledgerResponse = await api.get(`/api/cash-counter/member/${member.id}/ledger`);
            return { ...member, balance: ledgerResponse.data.summary.current_balance };
          } catch {
            return { ...member, balance: 0 };
          }
        })
      );

      setSearchResults(membersWithBalance);
    } catch (err) {
      console.error('Search failed', err);
    }
  };

  const handleSelectMember = async (member) => {
    try {
      const response = await api.get(`/api/cash-counter/member/${member.id}/ledger`);
      setSelectedMember({
        ...member,
        balance: response.data.summary.current_balance
      });
      setRecentTransactions(response.data.transactions.slice(0, 5));
      setSearch('');
      setSearchResults([]);
      setShowForm(true);
    } catch (err) {
      showError('Failed to load member details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMember) {
      showError('Please select a member');
      return;
    }
    const amount = parseFloat(formData.amount);
    if (!amount || amount <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/api/cash-counter/transactions', {
        member_id: selectedMember.id,
        ...formData,
        amount
      });

      showSuccess('Transaction recorded successfully');

      try {
        const doc = generateCashReceipt(response.data, selectedMember);
        doc.save(`receipt_${response.data.receipt_no}.pdf`);
      } catch (pdfError) {
        console.error('PDF generation error:', pdfError);
      }

      setShowForm(false);
      setSelectedMember(null);
      setRecentTransactions([]);
      setFormData({ transaction_type: 'Credit', payment_mode: 'Cash', amount: '', note: '' });
      fetchTransactions();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to record transaction');
    } finally {
      setSaving(false);
    }
  };

  const handlePrintReceipt = (transaction, member) => {
    try {
      const doc = generateCashReceipt(transaction, member || selectedMember);
      doc.save(`receipt_${transaction.receipt_no}.pdf`);
      showSuccess('Receipt downloaded successfully');
    } catch (err) {
      console.error('PDF error:', err);
      showError('Failed to generate receipt');
    }
  };

  const exportToExcel = () => {
    try {
      const exportData = transactions.map((txn, index) => ({
        'SL NO': index + 1,
        'RECEIPT NO': txn.receipt_no,
        'DATE': txn.created_at?.slice(0, 10),
        'TIME': txn.created_at?.slice(11, 16),
        'MEMBER NO': txn.member?.membership_no,
        'MEMBER NAME': txn.member?.applicant_name,
        'TYPE': txn.transaction_type,
        'AMOUNT': txn.amount,
        'BALANCE': txn.new_balance,
        'PAYMENT MODE': txn.payment_mode
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
      XLSX.writeFile(wb, `Transactions_${new Date().toISOString().split('T')[0]}.xlsx`);
      showSuccess('Excel exported successfully');
    } catch (err) {
      console.error('Excel export error:', err);
      showError('Failed to export Excel');
    }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('STATE BANK OF INDIA - CASH COUNTER', 105, 15, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Cash Transactions Report', 105, 25, { align: 'center' });
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 35);

      const tableData = transactions.map((txn, index) => [
        index + 1,
        txn.receipt_no,
        txn.created_at?.slice(0, 16).replace('T', ' '),
        `${txn.member?.applicant_name} (${txn.member?.membership_no})`,
        txn.transaction_type,
        `${txn.amount?.toLocaleString()}`,
        `${txn.new_balance?.toLocaleString()}`
      ]);

      autoTable(doc, {
        startY: 45,
        head: [['#', 'Receipt', 'Date/Time', 'Member', 'Type', 'Amount', 'Balance']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [0, 71, 143], fontSize: 9 },
        bodyStyles: { fontSize: 8 }
      });

      doc.save(`Transactions_${new Date().toISOString().split('T')[0]}.pdf`);
      showSuccess('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      showError('Failed to export PDF');
    }
  };

  if (loading) return <LoadingSpinner text="Loading transactions..." />;

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gray-50 min-h-screen">
      <GlobalStyles />
      
    
      {/* Search & Add Transaction */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-xl p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 shadow-lg">
        <h2 className="text-white text-base sm:text-lg md:text-xl font-semibold mb-3 sm:mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
          <span>New Cash Transaction</span>
        </h2>
        <div className="relative">
          <div className="relative w-full">
  <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 pointer-events-none z-10" />

  <Input
    placeholder="Search by Member No / Name / Mobile"
    value={search}
    onChange={(e) => handleSearch(e.target.value)}
    data-testid="member-search-input"
    className="
      h-10 sm:h-12 md:h-14
      !pl-11 sm:!pl-14
      pr-3 sm:pr-4
      py-0
      text-xs sm:text-sm md:text-base
      border-2 border-white
      rounded-lg
      bg-white
      focus:ring-2 focus:ring-blue-300
    "
  />
</div>


          {searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-2 bg-white border-2 border-blue-600 rounded-lg shadow-2xl max-h-60 sm:max-h-80 md:max-h-96 overflow-y-auto hide-scrollbar">
              {searchResults.map((member) => (
                <button
                  key={member.id}
                  onClick={() => handleSelectMember(member)}
                  className="w-full p-2 sm:p-3 md:p-4 text-left bg-white hover:bg-blue-50 border-b border-gray-200 transition-colors flex items-center gap-2 sm:gap-3"
                  data-testid={`search-result-${member.membership_no}`}
                >
                  {member.profile_picture ? (
                    <img
  src={resolveProfileImage(member.profile_picture)}
  alt=""
  className="w-10 h-10 rounded-full object-cover"
/>

                  ) : (
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <UserCircle className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-xs sm:text-sm md:text-base truncate">
                      {member.membership_no} | {member.applicant_name}
                    </p>
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 md:gap-4 text-xs sm:text-sm mt-0.5 sm:mt-1">
                      <span className="text-gray-600">Type: {member.membership_type}</span>
                      <span className={`font-semibold ${member.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        Balance: {(member.balance || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Transaction Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent 
          className="max-w-6xl max-h-[90vh] overflow-y-auto hide-scrollbar bg-white rounded-xl p-0 w-[95vw] sm:w-[90vw] md:w-[85vw]"
          data-testid="transaction-form-modal"
        >
          <DialogHeader className="border-b-2 border-blue-600 p-3 sm:p-4 md:p-6 bg-gradient-to-r from-blue-50 to-white">
            <DialogTitle className="text-base sm:text-xl md:text-2xl font-bold text-blue-900">
              💰 Cash Counter - New Transaction
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 p-3 sm:p-4 md:p-6">
            {/* Left Column - Form */}
            <div className="space-y-3 sm:space-y-4 md:space-y-6">
              {/* Selected Member Panel */}
              {selectedMember && (
                <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-600 rounded-xl p-3 sm:p-4 md:p-6">
                  <h3 className="text-xs sm:text-sm md:text-base font-semibold text-blue-900 mb-3 sm:mb-4">
                    Selected Member
                  </h3>
                  <div className="flex items-center gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4">
                    {selectedMember.profile_picture ? (
                      <img
                        src={selectedMember.profile_picture.startsWith('http') ? selectedMember.profile_picture : `${process.env.REACT_APP_BACKEND_URL}${selectedMember.profile_picture}`}
                        alt=""
                        className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full object-cover border-3 border-blue-600 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <UserCircle className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 truncate" data-testid="selected-member-name">
                        {selectedMember.applicant_name}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">Member No: {selectedMember.membership_no}</p>
                      <p className="text-xs sm:text-sm text-gray-600">Type: {selectedMember.membership_type}</p>
                      <p className="text-xs sm:text-sm text-gray-600">Mobile: {selectedMember.mobile}</p>
                    </div>
                  </div>
                  <div className={`${selectedMember.balance >= 0 ? 'bg-gradient-to-r from-green-600 to-green-500' : 'bg-gradient-to-r from-red-600 to-red-500'} p-3 sm:p-4 md:p-6 rounded-lg text-center text-white`}>
                    <p className="text-xs sm:text-sm font-medium mb-1 sm:mb-2">Current Balance</p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold" data-testid="current-balance">
                      {(selectedMember.balance || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              {/* Transaction Form */}
              <form onSubmit={handleSubmit}>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 md:space-y-6">
                  <h3 className="text-xs sm:text-sm md:text-base font-semibold text-blue-900 mb-3 sm:mb-4">
                    Transaction Details
                  </h3>

                  <div>
                    <Label className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
                      Transaction Type
                    </Label>
                    <RadioGroup
                      value={formData.transaction_type}
                      onValueChange={(v) => setFormData({ ...formData, transaction_type: v })}
                      className="grid grid-cols-2 gap-2 sm:gap-3"
                    >
                      <div className={`p-2 sm:p-3 md:p-4 border-2 border-green-600 rounded-lg cursor-pointer transition-all ${formData.transaction_type === 'Credit' ? 'bg-green-50' : 'bg-white'}`}>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="Credit" id="credit" data-testid="credit-radio" />
                          <Label htmlFor="credit" className="text-green-700 font-semibold cursor-pointer flex items-center gap-1 text-xs sm:text-sm">
                            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span>Credit</span>
                          </Label>
                        </div>
                      </div>
                      <div className={`p-2 sm:p-3 md:p-4 border-2 border-red-600 rounded-lg cursor-pointer transition-all ${formData.transaction_type === 'Debit' ? 'bg-red-50' : 'bg-white'}`}>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="Debit" id="debit" data-testid="debit-radio" />
                          <Label htmlFor="debit" className="text-red-700 font-semibold cursor-pointer flex items-center gap-1 text-xs sm:text-sm">
                            <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span>Debit</span>
                          </Label>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>

                  <div>
                    <Label className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
                      Payment Mode
                    </Label>
                    <RadioGroup
                      value={formData.payment_mode}
                      onValueChange={(v) => setFormData({ ...formData, payment_mode: v })}
                      className="grid grid-cols-2 gap-2 sm:gap-3"
                    >
                      {['Cash', 'Card', 'UPI', 'Net Banking'].map((mode) => (
                        <div key={mode} className={`p-2 sm:p-3 border-2 border-gray-300 rounded-lg cursor-pointer transition-all ${formData.payment_mode === mode ? 'bg-blue-50 border-blue-600' : 'bg-white'}`}>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value={mode} id={mode.toLowerCase().replace(' ', '-')} />
                            <Label htmlFor={mode.toLowerCase().replace(' ', '-')} className="cursor-pointer font-medium text-xs sm:text-sm">
                              {mode}
                            </Label>
                          </div>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>

                  <div>
                    <Label htmlFor="amount" className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
                      Amount () *
                    </Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="Enter amount"
                      className="h-10 sm:h-12 text-sm sm:text-base md:text-lg font-semibold border-2 border-gray-300 rounded-lg px-3 sm:px-4"
                      data-testid="amount-input"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="note" className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
                      Note (Optional)
                    </Label>
                    <Textarea
                      id="note"
                      value={formData.note}
                      onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                      placeholder="Add transaction note..."
                      rows={3}
                      className="border-2 border-gray-300 rounded-lg p-2 sm:p-3 text-xs sm:text-sm resize-none"
                      data-testid="note-textarea"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-3 sm:mt-4 md:mt-6">
                  <Button 
                    type="button" 
                    onClick={() => setShowForm(false)}
                    className="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-colors"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={saving}
                    className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-print-btn"
                  >
                    {saving ? 'Processing...' : 'Submit & Print'}
                  </Button>
                </div>
              </form>
            </div>

            {/* Right Column - Recent Transactions */}
            <div>
              <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-400 rounded-xl p-3 sm:p-4 md:p-6">
                <h3 className="text-xs sm:text-sm md:text-base font-semibold text-blue-900 mb-3 sm:mb-4">
                  Recent Transactions (Last 5)
                </h3>
                {recentTransactions.length > 0 ? (
                  <div className="overflow-x-auto hide-scrollbar">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-100">
                          <TableHead className="text-xs font-semibold text-gray-900 p-2">Date/Time</TableHead>
                          <TableHead className="text-xs font-semibold text-gray-900 p-2">Type</TableHead>
                          <TableHead className="text-xs font-semibold text-gray-900 p-2">Amount</TableHead>
                          <TableHead className="text-xs font-semibold text-gray-900 p-2">Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recentTransactions.map((txn) => (
                          <TableRow key={txn.id}>
                            <TableCell className="text-xs p-2">
                              {new Date(txn.created_at).toLocaleDateString()}<br/>
                              {new Date(txn.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </TableCell>
                            <TableCell className="p-2">
                              <Badge className={`${txn.transaction_type === 'Credit' ? 'bg-green-600' : 'bg-red-600'} text-white px-2 py-1 rounded text-xs font-semibold`}>
                                {txn.transaction_type}
                              </Badge>
                            </TableCell>
                            <TableCell className={`text-xs font-semibold p-2 ${txn.transaction_type === 'Credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {txn.transaction_type === 'Credit' ? '+' : '-'}{txn.amount?.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-xs font-semibold p-2">
                              {txn.new_balance?.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center py-6 sm:py-8 text-gray-500 text-xs sm:text-sm">
                    No recent transactions
                  </p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recent Transactions */}
      {error && <ErrorHandler error={error} onRetry={fetchTransactions} />}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-3 sm:p-4 md:p-6 border-b-2 border-blue-600">
          <h2 className="text-base sm:text-lg md:text-xl font-semibold text-white">
            Recent Transactions ({transactions.length})
          </h2>
        </div>
        <div className="p-0">
          {transactions.length > 0 ? (
            <div className="overflow-x-auto hide-scrollbar">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-100">
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Receipt</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm whitespace-nowrap">Date/Time</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Member</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Type</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Amount</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Balance</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Mode</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.slice(0, 20).map((txn) => (
                    <TableRow key={txn.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <TableCell className="font-semibold text-blue-700 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">
                        {txn.receipt_no}
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm p-2 sm:p-3 md:p-4 whitespace-nowrap">
                        {txn.created_at?.slice(0, 16).replace('T', ' ')}
                      </TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4">
                        <div className="flex items-center gap-2">
                          {txn.member?.profile_picture ? (
                            <img
                              src={txn.member.profile_picture.startsWith('http') ? txn.member.profile_picture : `${process.env.REACT_APP_BACKEND_URL}${txn.member.profile_picture}`}
                              alt=""
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                            />
                          ) : (
                            <UserCircle className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="font-semibold text-xs sm:text-sm truncate">
                              {txn.member?.applicant_name}
                            </p>
                            <p className="text-xs text-gray-600 truncate">
                              {txn.member?.membership_no}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4">
                        <Badge className={`${txn.transaction_type === 'Credit' ? 'bg-green-600' : 'bg-red-600'} text-white px-2 py-1 rounded-md font-semibold inline-flex items-center gap-1 text-xs whitespace-nowrap`}>
                          {txn.transaction_type === 'Credit' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          <span>{txn.transaction_type}</span>
                        </Badge>
                      </TableCell>
                      <TableCell className={`font-semibold text-xs sm:text-sm md:text-base ${txn.transaction_type === 'Credit' ? 'text-green-600' : 'text-red-600'} p-2 sm:p-3 md:p-4 whitespace-nowrap`}>
                        {txn.transaction_type === 'Credit' ? '+' : '-'}{txn.amount?.toLocaleString()}
                      </TableCell>
                      <TableCell className={`font-semibold text-xs sm:text-sm md:text-base ${txn.new_balance >= 0 ? 'text-green-600' : 'text-red-600'} p-2 sm:p-3 md:p-4 whitespace-nowrap`}>
                        {txn.new_balance?.toLocaleString()}
                      </TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4">
                        <Badge className="bg-white border border-gray-300 text-gray-700 px-2 py-1 rounded text-xs whitespace-nowrap">
                          {txn.payment_mode}
                        </Badge>
                      </TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4 text-center">
                        <Button
                          onClick={() => handlePrintReceipt(txn, txn.member)}
                          className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
                          title="Print Receipt"
                        >
                          <Printer className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState
              icon={Wallet}
              title="No transactions yet"
              description="Search for a member above to create a new transaction"
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== MEMBERS BALANCE PAGE ====================
const MembersBalancePage = () => {
  const { showSuccess, showError } = useToast();
  const isAdmin = useIsAdmin();
  
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [balanceFilter, setBalanceFilter] = useState('all');
  const [ledgerModal, setLedgerModal] = useState({ open: false, member: null, data: null });
  
  const [ledgerDateFrom, setLedgerDateFrom] = useState('');
  const [ledgerDateTo, setLedgerDateTo] = useState('');

  const [editModal, setEditModal] = useState({ open: false, transaction: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, transaction: null });
  const [editFormData, setEditFormData] = useState({
    transaction_type: '',
    amount: '',
    payment_mode: '',
    note: ''
  });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchMembersBalance();
  }, [balanceFilter]);

  const fetchMembersBalance = async () => {
    try {
      setLoading(true);
      const params = {};
      if (balanceFilter !== 'all') params.balance_type = balanceFilter;
      const response = await api.get('/api/cash-counter/members-balance', { params });
      setMembers(response.data);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewLedger = async (member) => {
    try {
      const params = {};
      if (ledgerDateFrom) params.date_from = ledgerDateFrom;
      if (ledgerDateTo) params.date_to = ledgerDateTo;
      const response = await api.get(`/api/cash-counter/member/${member.id}/ledger`, { params });
      setLedgerModal({ open: true, member, data: response.data });
    } catch (err) {
      showError('Failed to load ledger');
    }
  };

  const handleRefreshLedger = async () => {
    if (ledgerModal.member) {
      await handleViewLedger(ledgerModal.member);
    }
  };

  const handlePrintReceipt = (transaction) => {
    try {
      const doc = generateCashReceipt(transaction, ledgerModal.member);
      doc.save(`receipt_${transaction.receipt_no}.pdf`);
      showSuccess('Receipt downloaded');
    } catch (err) {
      console.error('PDF error:', err);
      showError('Failed to generate receipt');
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditFormData({
      transaction_type: transaction.transaction_type,
      amount: transaction.amount.toString(),
      payment_mode: transaction.payment_mode,
      note: transaction.note || ''
    });
    setEditModal({ open: true, transaction });
  };

  const handleUpdateTransaction = async () => {
    if (!editModal.transaction) return;

    const amount = parseFloat(editFormData.amount);
    if (!amount || amount <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    try {
      setUpdating(true);
      await api.put(`/api/cash-counter/transactions/${editModal.transaction.id}`, {
        ...editFormData,
        amount
      });
      showSuccess('Transaction updated successfully');
      setEditModal({ open: false, transaction: null });
      await handleRefreshLedger();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to update transaction');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteTransaction = async () => {
    if (!deleteDialog.transaction) return;

    try {
      await api.delete(`/api/cash-counter/transactions/${deleteDialog.transaction.id}`);
      showSuccess('Transaction deleted successfully');
      setDeleteDialog({ open: false, transaction: null });
      await handleRefreshLedger();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to delete transaction');
    }
  };

  const exportToExcel = () => {
    try {
      const exportData = filteredMembers.map((member, index) => ({
        'SL NO': index + 1,
        'MEMBER NO': member.membership_no,
        'NAME': member.applicant_name,
        'TYPE': member.membership_type,
        'MOBILE': member.mobile,
        'BALANCE': member.balance || 0,
        'TOTAL TRANSACTIONS': member.transaction_count || 0
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Members Balance');

      ws['!cols'] = [
        { wch: 6 }, { wch: 12 }, { wch: 25 }, { wch: 12 },
        { wch: 12 }, { wch: 15 }, { wch: 18 }
      ];

      XLSX.writeFile(wb, `Members_Balance_${new Date().toISOString().split('T')[0]}.xlsx`);
      showSuccess('Excel exported successfully');
    } catch (err) {
      console.error('Excel export error:', err);
      showError('Failed to export Excel');
    }
  };

  const exportLedgerToExcel = () => {
    if (!ledgerModal.data) return;

    try {
      const exportData = ledgerModal.data.transactions.map((txn, index) => ({
        'SL NO': index + 1,
        'RECEIPT NO': txn.receipt_no,
        'DATE': txn.created_at?.slice(0, 10),
        'TIME': txn.created_at?.slice(11, 16),
        'TYPE': txn.transaction_type,
        'AMOUNT': txn.amount,
        'BALANCE': txn.new_balance,
        'PAYMENT MODE': txn.payment_mode,
        'NOTE': txn.note || ''
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ledger');

      XLSX.writeFile(wb, `Ledger_${ledgerModal.member.membership_no}_${new Date().toISOString().split('T')[0]}.xlsx`);
      showSuccess('Ledger exported successfully');
    } catch (err) {
      console.error('Excel export error:', err);
      showError('Failed to export ledger');
    }
  };

  const exportLedgerToPDF = () => {
    if (!ledgerModal.data || !ledgerModal.member) return;

    try {
      const doc = generateLedgerReport(ledgerModal.member, ledgerModal.data);
      doc.save(`Ledger_${ledgerModal.member.membership_no}_${new Date().toISOString().split('T')[0]}.pdf`);
      showSuccess('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      showError('Failed to export PDF');
    }
  };

  const filteredMembers = members.filter(m => {
    const matchesSearch = !search ||
      m.membership_no?.toLowerCase().includes(search.toLowerCase()) ||
      m.applicant_name?.toLowerCase().includes(search.toLowerCase()) ||
      m.mobile?.includes(search);

    const matchesType = typeFilter === 'all' || m.membership_type === typeFilter;

    return matchesSearch && matchesType;
  });

  if (loading) return <LoadingSpinner text="Loading balances..." />;

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gray-50 min-h-screen">
      <GlobalStyles />
      {error && <ErrorHandler error={error} onRetry={fetchMembersBalance} />}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-3 sm:p-4 md:p-6">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <Tabs value={balanceFilter} onValueChange={setBalanceFilter}>
                <TabsList className="grid grid-cols-4 bg-white rounded-lg p-1 w-full sm:w-auto">
                  <TabsTrigger value="all" className="text-xs sm:text-sm font-semibold">All</TabsTrigger>
                  <TabsTrigger value="positive" className="text-xs sm:text-sm font-semibold text-green-700">Positive</TabsTrigger>
                  <TabsTrigger value="negative" className="text-xs sm:text-sm font-semibold text-red-700">Negative</TabsTrigger>
                  <TabsTrigger value="zero" className="text-xs sm:text-sm font-semibold">Zero</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full sm:w-40 bg-white text-xs sm:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="Permanent">Permanent</SelectItem>
                    <SelectItem value="Temporary">Temporary</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  onClick={exportToExcel}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                  data-testid="export-balance-excel-btn"
                >
                  <FileSpreadsheet className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Export</span>
                </Button>
              </div>
            </div>
            <div className="relative max-w-md">
  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />

  <Input
    placeholder="Search..."
    value={search}
    onChange={(e) => setSearch(e.target.value)}
    data-testid="balance-search-input"
    className="
      h-9 sm:h-10
      !pl-11 pr-3
      py-0
      text-xs sm:text-sm
      border-2 border-white
      rounded-lg
      bg-white
    "
  />
</div>

          </div>
        </div>
        <div className="p-0">
          {filteredMembers.length > 0 ? (
            <div className="overflow-x-auto hide-scrollbar">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-100">
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">#</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Photo</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm whitespace-nowrap">Member No</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Name</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Type</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Mobile</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Balance</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm whitespace-nowrap">Total Txn</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.map((member, index) => (
                    <TableRow key={member.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <TableCell className="font-semibold p-2 sm:p-3 md:p-4 text-xs sm:text-sm">{index + 1}</TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4">
                        {member.profile_picture ? (
                          <img
                            src={resolveProfileImage(member.profile_picture)}
                            alt=""
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-gray-200"
                          />
                        ) : (
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 flex items-center justify-center">
                            <UserCircle className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-semibold text-blue-700 p-2 sm:p-3 md:p-4 text-xs sm:text-sm whitespace-nowrap">
                        {member.membership_no}
                      </TableCell>
                      <TableCell className="font-semibold p-2 sm:p-3 md:p-4 text-xs sm:text-sm">{member.applicant_name}</TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4">
                        <Badge className={`${member.membership_type === 'Permanent' ? 'bg-blue-900' : 'bg-blue-600'} text-white px-2 py-1 rounded text-xs font-semibold whitespace-nowrap`}>
                          {member.membership_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4 text-xs sm:text-sm">{member.mobile}</TableCell>
                      <TableCell className={`font-bold text-sm sm:text-base md:text-lg ${member.balance >= 0 ? 'text-green-600' : 'text-red-600'} p-2 sm:p-3 md:p-4 whitespace-nowrap`}>
                        {(member.balance || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4">
                        <Badge className="bg-white border border-gray-300 text-gray-700 px-2 py-1 rounded text-xs sm:text-sm font-semibold">
                          {member.transaction_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4">
                        <Button
                          onClick={() => handleViewLedger(member)}
                          className="bg-blue-900 hover:bg-blue-800 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 transition-colors"
                          data-testid={`view-ledger-${member.membership_no}`}
                        >
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="whitespace-nowrap">View Ledger</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState icon={Wallet} title="No members found" description="No members match the filter criteria" />
          )}
        </div>
      </div>

      {/* Ledger Modal */}
      <Dialog open={ledgerModal.open} onOpenChange={(open) => !open && setLedgerModal({ open: false, member: null, data: null })}>
        <DialogContent 
          className="max-w-7xl max-h-[85vh] overflow-hidden flex flex-col bg-white rounded-xl p-0 w-[95vw] sm:w-[90vw]"
          data-testid="ledger-modal"
        >
          <DialogHeader className="border-b-2 border-blue-600 p-3 sm:p-4 md:p-6 bg-gradient-to-r from-blue-50 to-white">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <DialogTitle className="text-base sm:text-lg md:text-xl font-semibold text-blue-900">
                Member Ledger - {ledgerModal.member?.applicant_name} ({ledgerModal.member?.membership_no})
              </DialogTitle>
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={exportLedgerToExcel}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 transition-colors"
                  data-testid="export-ledger-excel-btn"
                >
                  <FileSpreadsheet className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Excel</span>
                </Button>
                <Button 
                  onClick={exportLedgerToPDF}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 transition-colors"
                  data-testid="export-ledger-pdf-btn"
                >
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>PDF</span>
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto hide-scrollbar p-3 sm:p-4 md:p-6">
            {ledgerModal.data && (
              <>
                {/* Date Range Filters */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-100 rounded-lg">
                  <div>
                    <Label htmlFor="ledger-date-from" className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
                      From Date
                    </Label>
                    <Input
                      id="ledger-date-from"
                      type="date"
                      value={ledgerDateFrom}
                      onChange={(e) => setLedgerDateFrom(e.target.value)}
                      className="border-2 border-gray-300 rounded-lg p-2 text-xs sm:text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ledger-date-to" className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
                      To Date
                    </Label>
                    <Input
                      id="ledger-date-to"
                      type="date"
                      value={ledgerDateTo}
                      onChange={(e) => setLedgerDateTo(e.target.value)}
                      className="border-2 border-gray-300 rounded-lg p-2 text-xs sm:text-sm"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={handleRefreshLedger}
                      className="bg-blue-900 hover:bg-blue-800 text-white px-4 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium w-full flex items-center justify-center gap-2 transition-colors"
                    >
                      <Search className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>Filter</span>
                    </Button>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-lg p-3 sm:p-4 md:p-6 text-center text-white">
                    <p className="text-xs sm:text-sm font-medium mb-1 sm:mb-2">Total Credit</p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold">
                      {(ledgerModal.data.summary.total_credit || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gradient-to-r from-red-600 to-red-500 rounded-lg p-3 sm:p-4 md:p-6 text-center text-white">
                    <p className="text-xs sm:text-sm font-medium mb-1 sm:mb-2">Total Debit</p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold">
                      {(ledgerModal.data.summary.total_debit || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className={`${ledgerModal.data.summary.current_balance >= 0 ? 'bg-gradient-to-r from-blue-900 to-blue-700' : 'bg-gradient-to-r from-red-600 to-red-500'} rounded-lg p-3 sm:p-4 md:p-6 text-center text-white`}>
                    <p className="text-xs sm:text-sm font-medium mb-1 sm:mb-2">Current Balance</p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold">
                      {(ledgerModal.data.summary.current_balance || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gradient-to-r from-orange-600 to-orange-500 rounded-lg p-3 sm:p-4 md:p-6 text-center text-white">
                    <p className="text-xs sm:text-sm font-medium mb-1 sm:mb-2">Transactions</p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold">
                      {ledgerModal.data.transactions?.length || 0}
                    </p>
                  </div>
                </div>

                {/* Transactions Table */}
                <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto hide-scrollbar overflow-x-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-gray-100 z-10">
                        <TableRow>
                          <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 text-xs sm:text-sm whitespace-nowrap">Receipt No</TableHead>
                          <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 text-xs sm:text-sm whitespace-nowrap">Date/Time</TableHead>
                          <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 text-xs sm:text-sm">Type</TableHead>
                          <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 text-xs sm:text-sm">Amount</TableHead>
                          <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 text-xs sm:text-sm">Balance</TableHead>
                          <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 text-xs sm:text-sm">Mode</TableHead>
                          {isAdmin && <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 text-xs sm:text-sm text-center">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ledgerModal.data.transactions.map((txn) => (
                          <TableRow key={txn.id} className="border-b border-gray-200 hover:bg-gray-50">
                            <TableCell className="font-semibold text-blue-700 p-2 sm:p-3 text-xs sm:text-sm whitespace-nowrap">
                              {txn.receipt_no}
                            </TableCell>
                            <TableCell className="text-xs sm:text-sm p-2 sm:p-3 whitespace-nowrap">
                              {txn.created_at?.slice(0, 16).replace('T', ' ')}
                            </TableCell>
                            <TableCell className="p-2 sm:p-3">
                              <Badge className={`${txn.transaction_type === 'Credit' ? 'bg-green-600' : 'bg-red-600'} text-white px-2 py-1 rounded text-xs font-semibold whitespace-nowrap`}>
                                {txn.transaction_type}
                              </Badge>
                            </TableCell>
                            <TableCell className={`font-semibold ${txn.transaction_type === 'Credit' ? 'text-green-600' : 'text-red-600'} p-2 sm:p-3 text-xs sm:text-sm whitespace-nowrap`}>
                              {txn.transaction_type === 'Credit' ? '+' : '-'}{txn.amount?.toLocaleString()}
                            </TableCell>
                            <TableCell className={`font-semibold ${txn.new_balance >= 0 ? 'text-green-600' : 'text-red-600'} p-2 sm:p-3 text-xs sm:text-sm whitespace-nowrap`}>
                              {txn.new_balance?.toLocaleString()}
                            </TableCell>
                            <TableCell className="p-2 sm:p-3">
                              <Badge className="bg-white border border-gray-300 text-gray-700 px-2 py-1 rounded text-xs whitespace-nowrap">
                                {txn.payment_mode}
                              </Badge>
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="p-2 sm:p-3">
                                <div className="flex gap-1 sm:gap-2 justify-center flex-wrap">
                                  <Button
                                    onClick={() => handlePrintReceipt(txn)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 sm:p-2 rounded-lg transition-colors"
                                    title="Print Receipt"
                                  >
                                    <Printer className="w-3 h-3 sm:w-4 sm:h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => handleEditTransaction(txn)}
                                    className="bg-yellow-500 hover:bg-yellow-600 text-white p-1.5 sm:p-2 rounded-lg transition-colors"
                                    title="Edit"
                                  >
                                    <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => setDeleteDialog({ open: true, transaction: txn })}
                                    className="bg-red-600 hover:bg-red-700 text-white p-1.5 sm:p-2 rounded-lg transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, transaction: null })}>
        <DialogContent className="max-w-md bg-white rounded-xl p-0 w-[95vw] sm:w-auto">
          <DialogHeader className="border-b-2 border-blue-600 p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-white">
            <DialogTitle className="text-base sm:text-lg font-semibold text-blue-900">
              Edit Transaction - {editModal.transaction?.receipt_no}
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 sm:p-6 space-y-4">
            <div>
              <Label className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
                Transaction Type
              </Label>
              <Select value={editFormData.transaction_type} onValueChange={(v) => setEditFormData({ ...editFormData, transaction_type: v })}>
                <SelectTrigger className="border-2 border-gray-300 rounded-lg text-xs sm:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Credit">Credit</SelectItem>
                  <SelectItem value="Debit">Debit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-amount" className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
                Amount ()
              </Label>
              <Input
                id="edit-amount"
                type="number"
                step="0.01"
                value={editFormData.amount}
                onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })}
                className="border-2 border-gray-300 rounded-lg p-2 sm:p-3 text-xs sm:text-sm"
              />
            </div>
            <div>
              <Label className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
                Payment Mode
              </Label>
              <Select value={editFormData.payment_mode} onValueChange={(v) => setEditFormData({ ...editFormData, payment_mode: v })}>
                <SelectTrigger className="border-2 border-gray-300 rounded-lg text-xs sm:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Card">Card</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Net Banking">Net Banking</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-note" className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
                Note
              </Label>
              <Textarea
                id="edit-note"
                value={editFormData.note}
                onChange={(e) => setEditFormData({ ...editFormData, note: e.target.value })}
                className="border-2 border-gray-300 rounded-lg p-2 sm:p-3 text-xs sm:text-sm resize-none"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-3 p-4 sm:p-6 bg-gray-50">
            <Button 
              onClick={() => setEditModal({ open: false, transaction: null })}
              className="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold transition-colors"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateTransaction} 
              disabled={updating}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, transaction: null })}>
        <AlertDialogContent className="bg-white rounded-xl max-w-md w-[95vw] sm:w-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base sm:text-lg font-semibold text-red-600">
              Are you sure?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs sm:text-sm text-gray-600 mt-2">
              This will permanently delete transaction <strong>{deleteDialog.transaction?.receipt_no}</strong>.
              This action cannot be undone and will affect the member's balance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
            <AlertDialogCancel className="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTransaction}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== REPORTS PAGE ====================
const ReportsPage = () => {
  const { showSuccess, showError } = useToast();
  const [reports, setReports] = useState({ transactions: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [txnType, setTxnType] = useState('All');
  const [selectedMember, setSelectedMember] = useState(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const handlePrintReceipt = (transaction, member) => {
    try {
      const doc = generateCashReceipt(transaction, member);
      doc.save(`receipt_${transaction.receipt_no}.pdf`);
      showSuccess('Receipt downloaded successfully');
    } catch (err) {
      console.error('PDF error:', err);
      showError('Failed to generate receipt');
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (txnType !== 'All') params.transaction_type = txnType;
      if (selectedMember) params.member_id = selectedMember.id;
      const response = await api.get('/api/cash-counter/reports', { params });
      setReports(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSearch = async (query) => {
    setSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await api.get('/api/members/search/autocomplete', { params: { q: query } });
      setSearchResults(response.data.filter(m => m.membership_type !== 'Guest'));
    } catch (err) {
      console.error('Search failed', err);
    }
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setSearch('');
    setSearchResults([]);
  };

  const clearMemberFilter = () => {
    setSelectedMember(null);
    fetchReports();
  };

  const exportToExcel = () => {
    try {
      const exportData = reports.transactions.map((txn, index) => ({
        'SL NO': index + 1,
        'RECEIPT NO': txn.receipt_no,
        'DATE': txn.created_at?.slice(0, 10),
        'TIME': txn.created_at?.slice(11, 16),
        'MEMBER NO': txn.member?.membership_no,
        'MEMBER NAME': txn.member?.applicant_name,
        'TYPE': txn.transaction_type,
        'AMOUNT': txn.amount,
        'BALANCE': txn.new_balance,
        'PAYMENT MODE': txn.payment_mode
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cash Counter Report');

      XLSX.writeFile(wb, `Cash_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      showSuccess('Excel exported successfully');
    } catch (err) {
      console.error('Excel export error:', err);
      showError('Failed to export Excel');
    }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();

      doc.setFontSize(16);
      doc.text('WILSON GARDEN CLUB', 105, 15, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Cash Counter Report', 105, 25, { align: 'center' });
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 35);

      const tableData = reports.transactions.map((txn, index) => [
        index + 1,
        txn.receipt_no,
        txn.created_at?.slice(0, 16).replace('T', ' '),
        `${txn.member?.applicant_name} (${txn.member?.membership_no})`,
        txn.transaction_type,
        `${txn.amount?.toLocaleString()}`,
        `${txn.new_balance?.toLocaleString()}`
      ]);

      autoTable(doc, {
        startY: 45,
        head: [['#', 'Receipt', 'Date/Time', 'Member', 'Type', 'Amount', 'Balance']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [0, 71, 143], fontSize: 9 },
        bodyStyles: { fontSize: 8 }
      });

      doc.save(`Cash_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      showSuccess('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      showError('Failed to export PDF');
    }
  };

  if (loading) return <LoadingSpinner text="Loading reports..." />;

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gray-50 min-h-screen">
      <GlobalStyles />
      
      {/* Filters */}
      <div className="bg-white rounded-xl p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 shadow-sm border border-gray-200">
        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-blue-900 mb-3 sm:mb-5">
          Report Filters
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          <div>
            <Label htmlFor="report-date-from" className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
              From Date
            </Label>
            <Input 
              id="report-date-from" 
              type="date" 
              value={dateFrom} 
              onChange={(e) => setDateFrom(e.target.value)} 
              className="border-2 border-gray-300 rounded-lg p-2 text-xs sm:text-sm"
              data-testid="date-from-input" 
            />
          </div>
          <div>
            <Label htmlFor="report-date-to" className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
              To Date
            </Label>
            <Input 
              id="report-date-to" 
              type="date" 
              value={dateTo} 
              onChange={(e) => setDateTo(e.target.value)} 
              className="border-2 border-gray-300 rounded-lg p-2 text-xs sm:text-sm"
              data-testid="date-to-input" 
            />
          </div>
          <div>
            <Label className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
              Transaction Type
            </Label>
            <Select value={txnType} onValueChange={setTxnType}>
              <SelectTrigger className="border-2 border-gray-300 rounded-lg text-xs sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="Credit">Credit</SelectItem>
                <SelectItem value="Debit">Debit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs sm:text-sm font-semibold text-gray-900 block mb-2">
              Member
            </Label>
            <div className="relative">
              <Input
                placeholder="Search member..."
                value={selectedMember ? selectedMember.applicant_name : search}
                onChange={(e) => handleMemberSearch(e.target.value)}
                className="border-2 border-gray-300 rounded-lg p-2 text-xs sm:text-sm"
                data-testid="member-filter-input"
              />
              {selectedMember && (
                <button
                  onClick={clearMemberFilter}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-600 hover:text-gray-900"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-xl max-h-48 overflow-y-auto hide-scrollbar">
                  {searchResults.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => handleSelectMember(member)}
                      className="w-full p-2 text-left bg-white hover:bg-blue-50 border-b border-gray-200 cursor-pointer text-xs sm:text-sm transition-colors"
                      onMouseEnter={(e) => e.currentTarget.style.background = '#EFF6FF'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#FFFFFF'}
                    >
                      {member.applicant_name} ({member.membership_no})
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-end">
            <Button 
              onClick={fetchReports}
              className="bg-blue-900 hover:bg-blue-800 text-white px-4 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-semibold w-full flex items-center justify-center gap-2 transition-colors"
              data-testid="generate-report-btn"
            >
              <Search className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Generate</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 mb-4 sm:mb-6">
        <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-xl p-4 sm:p-5 md:p-6 text-center text-white shadow-lg">
          <p className="text-xs sm:text-sm font-medium mb-2">Total Credit</p>
          <p className="text-2xl sm:text-3xl md:text-4xl font-bold">
            {(reports.summary.total_credit || 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-gradient-to-r from-red-600 to-red-500 rounded-xl p-4 sm:p-5 md:p-6 text-center text-white shadow-lg">
          <p className="text-xs sm:text-sm font-medium mb-2">Total Debit</p>
          <p className="text-2xl sm:text-3xl md:text-4xl font-bold">
            {(reports.summary.total_debit || 0).toLocaleString()}
          </p>
        </div>
        <div className={`${(reports.summary.net_amount || 0) >= 0 ? 'bg-gradient-to-r from-blue-900 to-blue-700' : 'bg-gradient-to-r from-red-600 to-red-500'} rounded-xl p-4 sm:p-5 md:p-6 text-center text-white shadow-lg`}>
          <p className="text-xs sm:text-sm font-medium mb-2">Net Amount</p>
          <p className="text-2xl sm:text-3xl md:text-4xl font-bold">
            {(reports.summary.net_amount || 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 md:p-6 bg-gradient-to-r from-blue-900 to-blue-700 gap-3">
          <h3 className="text-base sm:text-lg font-semibold text-white">
            Transactions ({reports.transactions?.length || 0})
          </h3>
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={exportToExcel}
              className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 transition-colors"
              data-testid="export-report-excel-btn"
            >
              <FileSpreadsheet className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Excel</span>
            </Button>
            <Button 
              onClick={exportToPDF}
              className="bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 transition-colors"
              data-testid="export-report-pdf-btn"
            >
              <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>PDF</span>
            </Button>
          </div>
        </div>
        <div className="p-0">
          {reports.transactions?.length > 0 ? (
            <div className="overflow-x-auto hide-scrollbar">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-100">
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">#</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm whitespace-nowrap">Receipt No</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm whitespace-nowrap">Date/Time</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Member</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Type</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Amount</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm">Balance</TableHead>
                    <TableHead className="font-semibold text-gray-900 p-2 sm:p-3 md:p-4 text-xs sm:text-sm text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.transactions.map((txn, index) => (
                    <TableRow key={txn.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <TableCell className="font-semibold p-2 sm:p-3 md:p-4 text-xs sm:text-sm">{index + 1}</TableCell>
                      <TableCell className="font-semibold text-blue-700 p-2 sm:p-3 md:p-4 text-xs sm:text-sm whitespace-nowrap">
                        {txn.receipt_no}
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm p-2 sm:p-3 md:p-4 whitespace-nowrap">
                        {txn.created_at?.slice(0, 16).replace('T', ' ')}
                      </TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                          {txn.member?.profile_picture ? (
                            <img
                              src={txn.member.profile_picture.startsWith('http') ? txn.member.profile_picture : `${process.env.REACT_APP_BACKEND_URL}${txn.member.profile_picture}`}
                              alt=""
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-gray-200"
                            />
                          ) : (
                            <UserCircle className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
                          )}
                          <div className="min-w-0">
                            <p className="font-semibold text-xs sm:text-sm truncate">
                              {txn.member?.applicant_name}
                            </p>
                            <p className="text-xs text-gray-600 truncate">
                              {txn.member?.membership_no}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4">
                        <Badge className={`${txn.transaction_type === 'Credit' ? 'bg-green-600' : 'bg-red-600'} text-white px-2 py-1 rounded-md font-semibold text-xs whitespace-nowrap`}>
                          {txn.transaction_type}
                        </Badge>
                      </TableCell>
                      <TableCell className={`font-semibold text-xs sm:text-sm md:text-base ${txn.transaction_type === 'Credit' ? 'text-green-600' : 'text-red-600'} p-2 sm:p-3 md:p-4 whitespace-nowrap`}>
                        {txn.transaction_type === 'Credit' ? '+' : '-'}{txn.amount?.toLocaleString()}
                      </TableCell>
                      <TableCell className={`font-semibold text-xs sm:text-sm md:text-base ${txn.new_balance >= 0 ? 'text-green-600' : 'text-red-600'} p-2 sm:p-3 md:p-4 whitespace-nowrap`}>
                        {txn.new_balance?.toLocaleString()}
                      </TableCell>
                      <TableCell className="p-2 sm:p-3 md:p-4 text-center">
                        <Button
                          onClick={() => handlePrintReceipt(txn, txn.member)}
                          className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
                          title="Print Receipt"
                          data-testid={`print-receipt-${txn.receipt_no}`}
                        >
                          <Printer className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState
              icon={Wallet}
              title="No transactions found"
              description="Adjust filters and generate report"
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN CASH COUNTER COMPONENT ====================
const CashCounter = () => {
  return (
    <LayoutWrapper
      title="Cash Counter"
      subtitle="Manage member cash transactions"
    >
      <Routes>
        <Route index element={<TransactionsPage />} />
        <Route path="balance" element={<MembersBalancePage />} />
        <Route path="reports" element={<ReportsPage />} />
      </Routes>
    </LayoutWrapper>
  );
};

export default CashCounter;
